import AddIcon from '@material-symbols/svg-300/rounded/add.svg'
import BlockIcon from '@material-symbols/svg-300/rounded/block.svg'
import RemoveIcon from '@material-symbols/svg-300/rounded/remove.svg'
import classnames from 'classnames'
import React, { useCallback } from 'react'

import useItemSelector, { ItemSelectorInit } from '../../../utilities/useItemSelector'
import Button from '../Button/Button'
import Combobox from '../Combobox/Combobox'
import FieldError from '../FieldError/FieldError'

import styles from './itemSelect.module.scss'

type UseSuggestionsHook<T> = () => {
  query: string | null | undefined
  setQuery: React.Dispatch<React.SetStateAction<string | null | undefined>>
  suggestions: T[]
}

export type MultiItemSelectProps<T extends { uuid: string }> = {
  items: T[]
  setItems: React.Dispatch<React.SetStateAction<T[]>>

  useSuggestions?: UseSuggestionsHook<T>
  mapSuggestion?: (user: T) => { disabled: boolean; info?: string }

  renderSelectedItemComponent?: React.FunctionComponent<{ item: T }>

  name?: string
  label?: string

  disabled?: boolean

  touched?: boolean
  error?: string
  omitErrorDisplay?: boolean

  onBlur?: React.FocusEventHandler<HTMLInputElement>
}

export type CreateItemSelectArgs<T extends { uuid: string }> = {
  multiSelect: boolean
  useSuggestions: UseSuggestionsHook<T>
  renderItemDescription: (item: T) => React.ReactNode
  renderSelectedItemComponent?: React.FunctionComponent<{ item: T }>
  renderEmptyComponent?: React.FunctionComponent
  className?: string
}

export default function multiItemSelect<T extends { uuid: string }>({
  multiSelect,
  useSuggestions: defaultSuggestionsHook,
  renderItemDescription,
  renderSelectedItemComponent: defaultRenderSelectedItemComponent,
  renderEmptyComponent,
  className,
}: CreateItemSelectArgs<T>) {
  const itemSelectorInit: ItemSelectorInit<T, T> = {
    createItem: (base) => ({ ...base }),
  }

  const baseClassName = classnames(styles.ItemSelect, className)

  return React.memo(function ({
    items,
    setItems,
    mapSuggestion,
    name,
    label,
    disabled,
    touched,
    error,
    omitErrorDisplay,
    onBlur,
    useSuggestions = defaultSuggestionsHook,
    renderSelectedItemComponent = defaultRenderSelectedItemComponent,
  }: MultiItemSelectProps<T>) {
    // Item suggestions
    const { query, setQuery, suggestions } = useSuggestions()
    const handleQueryChanged = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.currentTarget.value),
      [setQuery]
    )

    // Selected items
    const itemSelection = useItemSelector(itemSelectorInit, items, setItems, suggestions)

    const options = suggestions.map((item) => {
      const { disabled, info } = mapSuggestion?.(item) || {}
      return {
        value: item.uuid,
        label: (
          <>
            {disabled ? <BlockIcon /> : itemSelection.ids[item.uuid] ? <RemoveIcon /> : <AddIcon />}
            <span>
              {renderItemDescription(item)} {info && `(${info})`}
            </span>
          </>
        ),
        selected: itemSelection.ids[item.uuid],
        disabled,
      }
    })

    return (
      <div className={baseClassName}>
        <Combobox
          name={name}
          label={label}
          multiSelect={multiSelect}
          touched={touched}
          error={error}
          omitErrorDisplay
          options={options}
          value={query || ''}
          onChange={handleQueryChanged}
          onSelect={itemSelection.handleSelected}
          onBlur={onBlur}
          disabled={disabled}
        />

        {itemSelection.items.length !== 0 ? (
          <div className="selected-items">
            <ul>
              {itemSelection.items.map((item) => (
                <li key={item.uuid}>
                  <Button data-item-id={item.uuid} onClick={itemSelection.handleDeselected} small>
                    <RemoveIcon />
                  </Button>
                  {renderSelectedItemComponent
                    ? React.createElement(renderSelectedItemComponent, { item })
                    : renderItemDescription(item)}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          renderEmptyComponent && React.createElement(renderEmptyComponent)
        )}

        <FieldError error={error} touched={touched} omitErrorDisplay={omitErrorDisplay} />
      </div>
    )
  })
}
